import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RootLayout } from './layouts/root-layout';
import { AttendeeLayout } from './layouts/attendee-layout';
import { AdminLayout } from './layouts/admin-layout';
import { PlatformLayout } from './layouts/platform-layout';
import { useI18n } from './i18n/i18n-provider';
import {
  RequireAdminWorkspace,
  RequireAttendeeWorkspace,
  RequireAuthenticatedLayout,
  RequirePlatformWorkspace,
  RequirePrivacyRead,
  RequireBrandingManage,
} from './route-guards';

type PageModule<TName extends string> = Record<TName, ComponentType>;

function lazyRoute<TName extends string>(
  importer: () => Promise<PageModule<TName>>,
  exportName: TName,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => {
    const module = await importer();
    return { default: module[exportName] };
  });
}

function routeElement(Page: LazyExoticComponent<ComponentType>) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Page />
    </Suspense>
  );
}

function RouteFallback() {
  const { t } = useI18n();

  return (
    <div className="route-fallback" role="status" aria-live="polite">
      <span className="route-fallback__spinner" aria-hidden="true" />
      <span>{t('common.loading')}</span>
    </div>
  );
}

const HomePage = lazyRoute(() => import('./pages/home-page'), 'HomePage');
const PublicOrganizationPage = lazyRoute(() => import('./pages/public-organization-page'), 'PublicOrganizationPage');
const PublicAnalyticsPrivacyPage = lazyRoute(() => import('./pages/public-analytics-privacy-page'), 'PublicAnalyticsPrivacyPage');
const OrganizationAccountPage = lazyRoute(() => import('./pages/organization-account-page'), 'OrganizationAccountPage');
const HelpPage = lazyRoute(() => import('./pages/help-page'), 'HelpPage');
const OrganizationMembersPage = lazyRoute(() => import('./pages/admin/organization-members-page'), 'OrganizationMembersPage');
const OrganizationPrivacyPage = lazyRoute(() => import('./pages/admin/organization-privacy-page'), 'OrganizationPrivacyPage');
const OrganizationBrandingPage = lazyRoute(() => import('./pages/admin/organization-branding-page'), 'OrganizationBrandingPage');
const EventTypesPage = lazyRoute(() => import('./pages/admin/event-types-page'), 'EventTypesPage');
const SignInPage = lazyRoute(() => import('./pages/sign-in-page'), 'SignInPage');
const DevSignInPage = lazyRoute(
  () => import('./pages/dev-sign-in-page'),
  'DevSignInPage',
);
const AuthCallbackPage = lazyRoute(
  () => import('./pages/auth-callback-page'),
  'AuthCallbackPage',
);
const AttendeeHomePage = lazyRoute(
  () => import('./pages/attendee-home-page'),
  'AttendeeHomePage',
);
const AttendeeEventsPage = lazyRoute(
  () => import('./pages/attendee/attendee-events-page'),
  'AttendeeEventsPage',
);
const AttendeeEventDetailPage = lazyRoute(
  () => import('./pages/attendee/attendee-event-detail-page'),
  'AttendeeEventDetailPage',
);
const AttendeeEnrollmentsPage = lazyRoute(
  () => import('./pages/attendee/attendee-enrollments-page'),
  'AttendeeEnrollmentsPage',
);
const AttendeeTasksPage = lazyRoute(
  () => import('./pages/attendee/attendee-tasks-page'),
  'AttendeeTasksPage',
);
const AttendeeTaskDetailPage = lazyRoute(
  () => import('./pages/attendee/attendee-task-detail-page'),
  'AttendeeTaskDetailPage',
);
const AdminHomePage = lazyRoute(
  () => import('./pages/admin-home-page'),
  'AdminHomePage',
);
const AdminEventsPage = lazyRoute(
  () => import('./pages/admin/admin-events-page'),
  'AdminEventsPage',
);
const AdminEventCreatePage = lazyRoute(
  () => import('./pages/admin/admin-event-create-page'),
  'AdminEventCreatePage',
);
const AdminEventDetailPage = lazyRoute(
  () => import('./pages/admin/admin-event-detail-page'),
  'AdminEventDetailPage',
);
const AdminIdentityPage = lazyRoute(
  () => import('./pages/admin/admin-identity-page'),
  'AdminIdentityPage',
);
const AdminReportingIndexPage = lazyRoute(
  () => import('./pages/admin/admin-reporting-index-page'),
  'AdminReportingIndexPage',
);
const AdminEventReportingPage = lazyRoute(
  () => import('./pages/admin/admin-event-reporting-page'),
  'AdminEventReportingPage',
);
const OrganizerScannerPage = lazyRoute(
  () => import('./pages/admin/scanner/organizer-scanner-page'),
  'OrganizerScannerPage',
);
const SessionScannerPage = lazyRoute(() => import('./pages/admin/scanner/session-scanner-page'), 'SessionScannerPage');
const AdminAttendanceSessionsPage = lazyRoute(() => import('./pages/admin/admin-attendance-sessions-page'), 'AdminAttendanceSessionsPage');
const AttendanceRunReportingPage = lazyRoute(() => import('./pages/admin/attendance-run-reporting-page'), 'AttendanceRunReportingPage');
const AdminReviewQueuePage = lazyRoute(
  () => import('./pages/admin/admin-review-queue-page'),
  'AdminReviewQueuePage',
);
const AdminReviewSubmissionPage = lazyRoute(
  () => import('./pages/admin/admin-review-submission-page'),
  'AdminReviewSubmissionPage',
);
const AdminNotificationsPage = lazyRoute(
  () => import('./pages/admin/admin-notifications-page'),
  'AdminNotificationsPage',
);
const PlatformHomePage = lazyRoute(
  () => import('./pages/platform/platform-home-page'),
  'PlatformHomePage',
);
const PlatformTenantDetailPage = lazyRoute(
  () => import('./pages/platform/platform-tenant-detail-page'),
  'PlatformTenantDetailPage',
);
const NotFoundPage = lazyRoute(
  () => import('./pages/not-found-page'),
  'NotFoundPage',
);

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: routeElement(NotFoundPage),
    children: [
      { index: true, element: routeElement(HomePage) },
      { path: 'o/:slug', element: routeElement(PublicOrganizationPage) },
      { path: 'privacy/analytics', element: routeElement(PublicAnalyticsPrivacyPage) },
      { path: 'signin', element: routeElement(SignInPage) },
      { path: 'signin/dev', element: routeElement(DevSignInPage) },
      { path: 'auth/callback', element: routeElement(AuthCallbackPage) },
      {
        element: <RequireAuthenticatedLayout />,
        children: [
          { path: 'account', element: routeElement(OrganizationAccountPage) },
          { path: 'help', element: routeElement(HelpPage) },
          {
            element: <RequireAttendeeWorkspace />,
            children: [
              {
                path: 'attendee',
                element: <AttendeeLayout />,
                children: [
                  { index: true, element: routeElement(AttendeeHomePage) },
                  { path: 'events', element: routeElement(AttendeeEventsPage) },
                  {
                    path: 'events/:id',
                    element: routeElement(AttendeeEventDetailPage),
                  },
                  {
                    path: 'enrollments',
                    element: routeElement(AttendeeEnrollmentsPage),
                  },
                  { path: 'tasks', element: routeElement(AttendeeTasksPage) },
                  {
                    path: 'tasks/:enrollmentId/:assignmentId',
                    element: routeElement(AttendeeTaskDetailPage),
                  },
                ],
              },
            ],
          },
          {
            element: <RequireAdminWorkspace />,
            children: [
              {
                path: 'admin',
                element: <AdminLayout />,
                children: [
                  { index: true, element: routeElement(AdminHomePage) },
                  { path: 'events', element: routeElement(AdminEventsPage) },
                  {
                    path: 'events/new',
                    element: routeElement(AdminEventCreatePage),
                  },
                  {
                    path: 'events/:id',
                    element: routeElement(AdminEventDetailPage),
                  },
                  { path: 'identity', element: routeElement(AdminIdentityPage) },
                  { path: 'events/:id/attendance', element: routeElement(AdminAttendanceSessionsPage) },
                  { path: 'organization', element: routeElement(OrganizationMembersPage) },
                  { element: <RequirePrivacyRead />, children: [
                    { path: 'organization/privacy', element: routeElement(OrganizationPrivacyPage) },
                  ] },
                  { element: <RequireBrandingManage />, children: [
                    { path: 'organization/branding', element: routeElement(OrganizationBrandingPage) },
                  ] },
                  { path: 'event-types', element: routeElement(EventTypesPage) },
                  {
                    path: 'reporting',
                    element: routeElement(AdminReportingIndexPage),
                  },
                  {
                    path: 'reporting/:id',
                    element: routeElement(AdminEventReportingPage),
                  },
                  { path: 'reporting/:id/attendance', element: routeElement(AttendanceRunReportingPage) },
                  { path: 'scanner', element: routeElement(SessionScannerPage) },
                  { path: 'scanner/legacy', element: routeElement(OrganizerScannerPage) },
                  { path: 'reviews', element: routeElement(AdminReviewQueuePage) },
                  {
                    path: 'reviews/:submissionId',
                    element: routeElement(AdminReviewSubmissionPage),
                  },
                  {
                    path: 'notifications',
                    element: routeElement(AdminNotificationsPage),
                  },
                ],
              },
            ],
          },
          {
            element: <RequirePlatformWorkspace />,
            children: [
              {
                path: 'platform',
                element: <PlatformLayout />,
                children: [
                  { index: true, element: routeElement(PlatformHomePage) },
                  {
                    path: 'tenants/:id',
                    element: routeElement(PlatformTenantDetailPage),
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: routeElement(NotFoundPage) },
    ],
  },
];

export const router = createBrowserRouter(appRoutes);
